import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star, Bell, MessageCircle, Calendar, ShoppingBag, TrendingUp, Users, ArrowRight, Sparkles, Clock, Phone, X, Zap, Shield, Play, MapPin, Heart, Store, Package, Target, Sun, Moon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useTheme } from '../context/ThemeContext';

// Logos
const LOGOS = {
  dark: 'https://customer-assets.emergentagent.com/job_store-boost-1/artifacts/p00q8cnd_logo_darkversion.6219654a445e56ed3cd0.png',
  light: 'https://customer-assets.emergentagent.com/job_store-boost-1/artifacts/40mbqv8p_logo.a291411cbf7fc3847246.png'
};

// Real images from Pexels
const IMAGES = {
  barberShop: 'https://images.pexels.com/photos/4625624/pexels-photo-4625624.jpeg?auto=compress&cs=tinysrgb&w=800',
  barberCutting: 'https://images.pexels.com/photos/7697443/pexels-photo-7697443.jpeg?auto=compress&cs=tinysrgb&w=800',
  beautySalon: 'https://images.pexels.com/photos/3736519/pexels-photo-3736519.jpeg?auto=compress&cs=tinysrgb&w=800',
  restaurant: 'https://images.pexels.com/photos/5216394/pexels-photo-5216394.jpeg?auto=compress&cs=tinysrgb&w=800',
  barberClose: 'https://images.pexels.com/photos/1319462/pexels-photo-1319462.jpeg?auto=compress&cs=tinysrgb&w=800',
  spaInterior: 'https://images.pexels.com/photos/13068380/pexels-photo-13068380.jpeg?auto=compress&cs=tinysrgb&w=800',
  shop: 'https://images.pexels.com/photos/1884581/pexels-photo-1884581.jpeg?auto=compress&cs=tinysrgb&w=800'
};

const Homepage = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 45, seconds: 30 });
  const [activeNotification, setActiveNotification] = useState(0);
  const [mockupType, setMockupType] = useState('services');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showElitModal, setShowElitModal] = useState(false);
  const [upgradeSelected, setUpgradeSelected] = useState(false);
  const [heroElitSelected, setHeroElitSelected] = useState(true); // Pre-selezionato per default

  // Theme classes
  const t = {
    bg: isDark ? 'bg-[#0a0f1a]' : 'bg-slate-50',
    bgCard: isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200 shadow-sm',
    bgCardHover: isDark ? 'hover:border-emerald-500/50 hover:bg-emerald-500/5' : 'hover:border-emerald-400 hover:shadow-md',
    text: isDark ? 'text-white' : 'text-slate-900',
    textSecondary: isDark ? 'text-slate-300' : 'text-slate-600',
    textMuted: isDark ? 'text-slate-400' : 'text-slate-500',
    border: isDark ? 'border-slate-700' : 'border-slate-200',
    switchBg: isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm',
    switchInactive: isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900',
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { hours: prev.hours, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveNotification(prev => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleMobileCheckout = () => setShowUpgradeModal(true);

  const handleUpgradeDecision = (wantsUpgrade) => {
    setUpgradeSelected(wantsUpgrade);
    setShowUpgradeModal(false);
    setTimeout(() => setShowElitModal(true), 300);
  };

  const handleElitDecision = (wantsElit) => {
    setShowElitModal(false);
    const planData = {
      type: selectedPlan,
      basePlan: upgradeSelected ? 'premium' : 'visibility',
      price: selectedPlan === 'yearly' ? (upgradeSelected ? 214.90 : 49.90) : (upgradeSelected ? 19.90 : 4.99),
      upgradeToPremium: upgradeSelected,
      addElit: wantsElit
    };
    setTimeout(() => navigate('/checkout', { state: planData }), 300);
  };

  const handleContinue = () => {
    if (window.innerWidth < 768) {
      handleMobileCheckout();
      return;
    }
    navigate('/checkout', { state: { type: selectedPlan, basePlan: 'visibility', price: selectedPlan === 'yearly' ? 49.90 : 4.99 }});
  };

  return (
    <div className={`min-h-screen overflow-hidden transition-colors duration-300 ${t.bg}`}>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 py-2 px-4">
        <div className="container mx-auto max-w-7xl">
          <p className="text-white text-center text-sm md:text-base font-medium">
            🎯 <span className="font-bold">50.000+ persone nella tua zona</span> cercano servizi e prodotti ogni mese. <span className="underline cursor-pointer" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}>Fatti trovare →</span>
          </p>
        </div>
      </div>

      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className={`absolute w-[800px] h-[800px] rounded-full blur-[120px] -top-96 -left-48 animate-pulse ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-500/5'}`} />
        <div className={`absolute w-[600px] h-[600px] rounded-full blur-[100px] top-1/2 -right-48 animate-pulse ${isDark ? 'bg-cyan-500/10' : 'bg-cyan-500/5'}`} style={{ animationDelay: '1s' }} />
      </div>

      {/* Hero Section */}
      <section className="relative flex items-center justify-center py-8 md:py-16 px-4">
        <div className="container mx-auto max-w-7xl relative z-10">
          {/* Logo */}
          <div className="text-center mb-8 md:mb-12 animate-fade-in">
            <img src={isDark ? LOGOS.dark : LOGOS.light} alt="Skipres" className="h-10 md:h-14 mx-auto" />
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Copy */}
            <div className="space-y-5 md:space-y-6 animate-fade-in-left text-center lg:text-left" style={{ animationDelay: '0.2s' }}>
              <div>
                <Badge className={`mb-3 px-3 py-1 text-xs md:text-sm ${isDark ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
                  <Sparkles className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                  €10M+ generati sul nostro portale
                </Badge>
                
                <h2 className={`text-3xl md:text-4xl lg:text-5xl font-black leading-[1.1] mb-4 ${t.text}`}>
                  I Tuoi Nuovi Clienti Ti Stanno{' '}
                  <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Cercando</span>
                </h2>

                <p className={`text-base md:text-lg leading-relaxed ${t.textSecondary}`}>
                  Ogni giorno, <span className="text-emerald-500 font-semibold">50.000+ persone</span> stanno 
                  cercando servizi e prodotti come i tuoi su Skipres. <span className={`font-semibold ${t.text}`}>Non farti trovare impreparato.</span>
                </p>
              </div>

              {/* Stats - 4 in una riga */}
              <div className="grid grid-cols-3 gap-1.5 md:gap-3">
                {[
                  { icon: Users, value: '50K+', label: 'Persone che acquistano ogni singolo giorno' },
                  { icon: Store, value: '100+', label: 'Aziende e Professionisti già presenti sul portale' },
                  { icon: Calendar, value: '2M+', label: 'Prenotazioni e Vendite gestite in soli 2 anni' }
                ].map((stat, i) => (
                  <div key={i} className={`group rounded-xl p-2 md:p-3 text-center border transition-all duration-500 ${t.bgCard} ${t.bgCardHover}`}>
                    <stat.icon className="w-4 h-4 md:w-5 md:h-5 text-emerald-500 mx-auto mb-0.5 md:mb-1 group-hover:scale-110 transition-transform" />
                    <div className={`text-sm md:text-xl font-black group-hover:text-emerald-500 transition-colors ${t.text}`}>{stat.value}</div>
                    <div className={`text-[8px] md:text-xs ${t.textMuted}`}>{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Benefit badge - Garanzia Elit */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-2">
                <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 border transition-all duration-300 ${isDark ? 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/50' : 'bg-emerald-50 border-emerald-200 hover:border-emerald-300'}`}>
                  <Shield className="w-4 h-4 text-emerald-500" />
                  <span className={`text-xs md:text-sm font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    Elit: 0 risultati = Rimborso 100%
                  </span>
                </div>
              </div>

              {/* CTA */}
              <div className="space-y-2">
                <Button 
                  size="lg"
                  onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                  className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white text-base md:text-lg px-8 py-6 rounded-xl shadow-xl hover:shadow-emerald-500/30 transition-all hover:scale-[1.02] group"
                  data-testid="hero-cta-button"
                >
                  🚀 Attiva La Tua Vetrina
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                
                <p className={`text-xs md:text-sm flex items-center gap-3 justify-center lg:justify-start ${t.textMuted}`}>
                  <span><span className="line-through">€9,99</span> <span className="text-emerald-500 font-bold">€4,99/mese</span></span>
                  <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Annulla quando vuoi</span>
                </p>
              </div>
            </div>

            {/* Right: Mockup with Switch */}
            <div className="relative animate-fade-in-right" style={{ animationDelay: '0.4s' }}>
              <div className="flex justify-center mb-4">
                <div className={`backdrop-blur-sm rounded-full p-1 inline-flex border ${t.switchBg}`}>
                  <button onClick={() => setMockupType('services')} className={`flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm transition-all ${mockupType === 'services' ? 'bg-emerald-500 text-white shadow-lg' : t.switchInactive}`}>
                    <Calendar className="w-3.5 h-3.5" /> Offro Servizi
                  </button>
                  <button onClick={() => setMockupType('products')} className={`flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm transition-all ${mockupType === 'products' ? 'bg-emerald-500 text-white shadow-lg' : t.switchInactive}`}>
                    <Package className="w-3.5 h-3.5" /> Offro Prodotti
                  </button>
                </div>
              </div>
              <InteractivePhoneMockup activeNotification={activeNotification} mockupType={mockupType} />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof - Trust Badges */}
      <section className={`relative py-6 md:py-8 border-y ${isDark ? 'bg-slate-900/50 border-slate-800' : 'bg-slate-50 border-slate-200'}`}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {[
              { icon: '🏆', title: 'Piattaforma #1', subtitle: 'in Italia', color: 'amber' },
              { icon: '🛡️', title: 'Garanzia Elit', subtitle: 'Rimborso 100%', color: 'emerald' },
              { icon: '🔒', title: 'Pagamenti', subtitle: '100% Sicuri', color: 'blue' },
              { icon: '💬', title: 'Supporto', subtitle: 'H24 7/7', color: 'purple' }
            ].map((item, i) => (
              <div key={i} className={`flex items-center gap-3 p-3 md:p-4 rounded-xl border transition-all ${isDark ? 'bg-slate-800/50 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'}`}>
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center text-xl md:text-2xl ${
                  item.color === 'amber' ? isDark ? 'bg-amber-500/20' : 'bg-amber-100' :
                  item.color === 'emerald' ? isDark ? 'bg-emerald-500/20' : 'bg-emerald-100' :
                  item.color === 'blue' ? isDark ? 'bg-blue-500/20' : 'bg-blue-100' :
                  isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                }`}>
                  {item.icon}
                </div>
                <div>
                  <div className={`font-bold text-sm md:text-base ${t.text}`}>{item.title}</div>
                  <div className={`text-[10px] md:text-xs ${t.textMuted}`}>{item.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Desktop Mockup Section */}
      <section id="vetrina" className="py-12 md:py-20 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <Badge className={`mb-3 px-3 py-1 ${isDark ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
              <Play className="w-3 h-3 mr-1" /> Preview della Tua Vetrina
            </Badge>
            <h2 className={`text-2xl md:text-4xl font-black mb-3 ${t.text}`}>
              Ecco Come Ti Vedranno <span className="text-emerald-500">50.000 Clienti</span>
            </h2>
            <p className={`text-base md:text-lg max-w-2xl mx-auto ${t.textMuted}`}>Per professionisti, negozi e aziende: una vetrina che lavora 24/7</p>
            
            <div className="flex justify-center mt-6">
              <div className={`backdrop-blur-sm rounded-full p-1 inline-flex border ${t.switchBg}`}>
                <button onClick={() => setMockupType('services')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${mockupType === 'services' ? 'bg-emerald-500 text-white shadow-lg' : t.switchInactive}`}>
                  <Calendar className="w-4 h-4" /> Offro Servizi
                </button>
                <button onClick={() => setMockupType('products')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${mockupType === 'products' ? 'bg-emerald-500 text-white shadow-lg' : t.switchInactive}`}>
                  <Package className="w-4 h-4" /> Offro Prodotti
                </button>
              </div>
            </div>
          </div>
          <div className="max-w-5xl mx-auto">
            <DesktopMockup mockupType={mockupType} isDark={isDark} />
          </div>
        </div>
      </section>

      {/* Live Notifications */}
      <section className={`py-12 md:py-20 relative ${isDark ? 'bg-slate-900/50' : 'bg-slate-100/50'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <Badge className={`mb-3 px-3 py-1 ${isDark ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
              <Bell className="w-3 h-3 mr-1 animate-bounce" /> In Tempo Reale
            </Badge>
            <h2 className={`text-2xl md:text-4xl font-black mb-3 ${t.text}`}>Mentre riposi, <span className="text-emerald-500">Skipres Lavora</span></h2>
            <p className={`text-base md:text-lg max-w-2xl mx-auto ${t.textMuted}`}>Ricevi notifiche per ogni prenotazione, vendita e recensione</p>
          </div>
          <LiveNotificationsDemo isDark={isDark} t={t} />
        </div>
      </section>

      {/* Before/After */}
      <section className="py-12 md:py-20 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <Badge className={`mb-3 px-3 py-1 ${isDark ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>Trasformazione</Badge>
            <h2 className={`text-2xl md:text-4xl font-black mb-3 ${t.text}`}>Da Invisibile a <span className="text-emerald-500">Irresistibile</span></h2>
          </div>
          <BeforeAfterSection isDark={isDark} t={t} />
        </div>
      </section>

      {/* Testimonials */}
      <section className={`py-12 md:py-20 relative ${isDark ? 'bg-slate-900/50' : 'bg-slate-100/50'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <Badge className={`mb-3 px-3 py-1 ${isDark ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
              <Star className="w-3 h-3 mr-1" /> Storie di Successo
            </Badge>
            <h2 className={`text-2xl md:text-4xl font-black mb-3 ${t.text}`}>Loro Hanno Già <span className="text-emerald-500">Svoltato</span></h2>
          </div>
          <TestimonialsSection isDark={isDark} t={t} />
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-12 md:py-20 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/30 rounded-full px-4 py-2 mb-4 animate-pulse">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-ping" />
                <span className="text-red-400 font-bold text-sm">🔥 OFFERTA: {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}</span>
              </div>
              <h2 className={`text-2xl md:text-4xl font-black mb-3 ${t.text}`}>Inizia a <span className="text-emerald-500">Crescere</span> Oggi</h2>
              <p className={`text-base md:text-lg ${t.textMuted}`}>Un caffè al giorno per trasformare il tuo business</p>
            </div>

            <div className="flex justify-center mb-8">
              <div className={`backdrop-blur-sm rounded-full p-1 inline-flex border ${t.switchBg}`}>
                <button onClick={() => setSelectedPlan('monthly')} className={`px-6 md:px-8 py-2.5 rounded-full text-sm font-semibold transition-all ${selectedPlan === 'monthly' ? 'bg-emerald-500 text-white shadow-lg' : t.switchInactive}`}>Mensile</button>
                <button onClick={() => setSelectedPlan('yearly')} className={`px-6 md:px-8 py-2.5 rounded-full text-sm font-semibold transition-all relative ${selectedPlan === 'yearly' ? 'bg-emerald-500 text-white shadow-lg' : t.switchInactive}`}>
                  Annuale
                  <span className="absolute -top-7 left-1/2 -translate-x-1/2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">-50%</span>
                </button>
              </div>
            </div>

            <PricingCard selectedPlan={selectedPlan} onContinue={handleContinue} isDark={isDark} t={t} />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={`py-12 md:py-20 relative ${isDark ? 'bg-slate-900/50' : 'bg-slate-100/50'}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <Badge className={`mb-3 px-3 py-1 ${isDark ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
                <MessageCircle className="w-3 h-3 mr-1" /> Domande Frequenti
              </Badge>
              <h2 className={`text-2xl md:text-4xl font-black mb-3 ${t.text}`}>
                Hai Qualche <span className="text-emerald-500">Dubbio?</span>
              </h2>
              <p className={`text-base md:text-lg ${t.textMuted}`}>Ecco le risposte alle domande più comuni</p>
            </div>
            <FAQSection isDark={isDark} t={t} />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-20 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto relative">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-3xl blur-3xl" />
            <div className={`relative backdrop-blur-xl border rounded-2xl p-8 md:p-12 text-center ${isDark ? 'bg-slate-900/90 border-emerald-500/30' : 'bg-white/90 border-emerald-200'}`}>
              <div className="text-5xl mb-4">🚀</div>
              <h2 className={`text-2xl md:text-4xl font-black mb-4 ${t.text}`}>Il Momento è <span className="text-emerald-500">Adesso</span></h2>
              <p className={`text-base md:text-lg mb-6 max-w-xl mx-auto ${t.textSecondary}`}>
                Ogni minuto che passa, i tuoi concorrenti stanno acquisendo i clienti che potrebbero essere tuoi.
                <span className="block mt-2 text-emerald-500 font-semibold">Non restare indietro.</span>
              </p>
              <Button size="lg" onClick={handleContinue} className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white text-lg md:text-xl px-10 py-6 rounded-full shadow-xl hover:shadow-emerald-500/30 transition-all hover:scale-105 group">
                <span className="line-through opacity-60 mr-2">€9,99</span> Solo €4,99/mese
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className={`text-xs md:text-sm mt-4 flex flex-wrap items-center justify-center gap-3 ${t.textMuted}`}>
                <span>✓ Setup in 5 minuti</span><span>✓ Annulla quando vuoi</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Modals */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className={`rounded-t-2xl md:rounded-3xl w-full max-w-md p-5 animate-slide-up ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className={`text-xl font-black ${t.text}`}>Vuoi di Più?</h3>
              <p className={`text-xs ${t.textMuted}`}>Passa al Piano Gestionale e gestisci tutto</p>
            </div>
            
            {/* Mockup Dashboard */}
            <div className="flex gap-2 mb-4 justify-center">
              {/* Desktop mockup */}
              <div className={`flex-1 max-w-[140px] rounded-lg p-2 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                <div className={`rounded ${isDark ? 'bg-slate-900' : 'bg-white'} p-2`}>
                  <div className="flex gap-1 mb-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400"></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-400"></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
                  </div>
                  <div className="grid grid-cols-3 gap-1 mb-2">
                    <div className={`rounded p-1 ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                      <div className="text-purple-500 text-[9px] font-bold">€2.4K</div>
                      <div className={`text-[6px] ${t.textMuted}`}>Oggi</div>
                    </div>
                    <div className={`rounded p-1 ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                      <div className="text-emerald-500 text-[9px] font-bold">+32%</div>
                      <div className={`text-[6px] ${t.textMuted}`}>Crescita</div>
                    </div>
                    <div className={`rounded p-1 ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                      <div className="text-blue-500 text-[9px] font-bold">156</div>
                      <div className={`text-[6px] ${t.textMuted}`}>Clienti</div>
                    </div>
                  </div>
                  <div className={`h-8 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-50'} flex items-end px-1 gap-0.5`}>
                    {[40, 65, 45, 80, 60, 90, 75].map((h, i) => (
                      <div key={i} className="flex-1 bg-purple-500 rounded-t" style={{height: `${h}%`}}></div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Phone mockup */}
              <div className={`w-14 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-slate-200'} p-1`}>
                <div className={`rounded-lg ${isDark ? 'bg-slate-900' : 'bg-white'} h-full p-1.5`}>
                  <div className={`w-4 h-1 mx-auto rounded ${isDark ? 'bg-slate-700' : 'bg-slate-300'} mb-2`}></div>
                  <div className={`rounded p-1 mb-1.5 ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                    <div className="text-purple-500 text-[8px] font-bold text-center">€2.4K</div>
                  </div>
                  <div className={`h-6 rounded ${isDark ? 'bg-slate-800' : 'bg-slate-50'} flex items-end px-0.5 gap-px`}>
                    {[50, 70, 40, 90, 60].map((h, i) => (
                      <div key={i} className="flex-1 bg-purple-500 rounded-t" style={{height: `${h}%`}}></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`rounded-xl p-3 mb-4 border ${isDark ? 'bg-slate-800/50 border-purple-500/30' : 'bg-purple-50 border-purple-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`font-bold text-sm ${t.text}`}>Piano Gestionale</span>
                <div><span className={`line-through text-xs ${t.textMuted}`}>€39,90</span><span className="text-purple-500 font-black text-lg ml-1">€19,90</span><span className={`text-xs ${t.textMuted}`}>/mese</span></div>
              </div>
              <div className="space-y-1.5 text-xs">
                {['Gestione agenda completa', 'CRM clienti integrato', 'Statistiche avanzate'].map((f, i) => (
                  <div key={i} className={`flex items-center gap-2 ${t.textSecondary}`}><Check className="w-3 h-3 text-purple-500" />{f}</div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Button onClick={() => handleUpgradeDecision(true)} className="w-full bg-purple-500 hover:bg-purple-400 text-white py-3 rounded-xl font-bold text-sm">Sì, Voglio il Gestionale! 🚀</Button>
              <button onClick={() => handleUpgradeDecision(false)} className={`w-full py-2 text-xs transition-colors ${t.textMuted} hover:${t.text}`}>No grazie, continuo con Solo Visibilità</button>
            </div>
          </div>
        </div>
      )}

      {showElitModal && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className={`rounded-t-2xl md:rounded-3xl w-full max-w-md p-4 pb-6 animate-slide-up ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
            <div className="text-center mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Star className="w-5 h-5 text-white" />
              </div>
              <h3 className={`text-lg font-black ${t.text}`}>Programma Elit</h3>
              <Badge className="bg-green-500 text-white border-0 text-[10px] px-2 py-0.5 mt-1">100% GRATUITO</Badge>
            </div>
            
            {/* Mockup Lista Attività con Elit in evidenza */}
            <div className={`rounded-xl p-2 mb-3 mx-auto max-w-[180px] ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
              <div className={`rounded-lg ${isDark ? 'bg-slate-900' : 'bg-white'} p-2`}>
                <div className={`w-8 h-1.5 mx-auto rounded ${isDark ? 'bg-slate-700' : 'bg-slate-300'} mb-2`}></div>
                <div className={`text-[8px] mb-2 px-1 ${t.textMuted}`}>🔍 Cerca servizi...</div>
                {/* Elit business - highlighted */}
                <div className={`rounded-lg p-2 mb-1.5 border-2 border-emerald-500 ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500"></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-1">
                        <span className={`text-[9px] font-bold ${t.text}`}>Barber Top</span>
                        <span className="text-[6px] bg-emerald-500 text-white px-1 py-0.5 rounded font-bold">⭐ GENIUS</span>
                      </div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        <span className="text-amber-400 text-[8px]">★★★★★</span>
                        <span className={`text-[7px] ${t.textMuted}`}>4.9</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Other businesses - faded */}
                {[1,2].map(i => (
                  <div key={i} className={`rounded p-1.5 mb-1 opacity-40 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-4 h-4 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-300'}`}></div>
                      <div className="flex-1">
                        <div className={`text-[7px] ${t.textMuted}`}>Altra Attività</div>
                        <div className={`text-[6px] ${t.textMuted}`}>★★★★☆</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className={`rounded-lg p-3 mb-3 border ${isDark ? 'bg-slate-800/50 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}>
              <p className={`text-xs mb-2 ${t.textSecondary}`}>
                Noi investiamo in <span className="text-amber-500 font-semibold">pubblicità</span> per portarti nuovi clienti. Tu paghi <span className="text-amber-500 font-semibold">solo il 20%</span> sui risultati.
              </p>
              <div className="space-y-1 text-xs">
                {['Zero costi fissi', 'Pubblicità su Google e Social', 'Visibilità prioritaria'].map((f, i) => (
                  <div key={i} className={`flex items-center gap-1.5 ${t.textSecondary}`}><Check className="w-3 h-3 text-amber-500" />{f}</div>
                ))}
              </div>
            </div>
            
            {/* Garanzia */}
            <div className={`rounded-lg p-3 mb-3 border-2 ${isDark ? 'bg-emerald-500/10 border-emerald-500/50' : 'bg-emerald-50 border-emerald-400'}`}>
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className={`font-bold text-sm ${t.text}`}>🛡️ Garanzia 6 Mesi</div>
                  <p className={`text-[10px] leading-tight ${t.textSecondary}`}>
                    Se non ricevi <span className="text-emerald-500 font-semibold">almeno 1 prenotazione/vendita</span> entro 6 mesi, ti <span className="text-emerald-500 font-semibold">rimborsiamo tutto</span>. Zero rischi!
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Button onClick={() => handleElitDecision(true)} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white py-2.5 rounded-lg font-bold text-sm">Attiva Elit! ⭐</Button>
              <button onClick={() => handleElitDecision(false)} className={`w-full py-2 text-xs transition-colors ${t.textMuted}`}>No grazie, continua →</button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className={`py-4 border-t transition-colors ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] md:text-xs transition-all border ${t.bgCard}`} aria-label="Toggle theme">
              {isDark ? <><Sun className="w-3 h-3" /><span>Light</span></> : <><Moon className="w-3 h-3" /><span>Dark</span></>}
            </button>
            <div className={`text-[10px] md:text-sm ${t.textMuted}`}>© 2026 Skipres</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Phone Mockup
const InteractivePhoneMockup = ({ activeNotification, mockupType }) => {
  const notifications = mockupType === 'services' 
    ? [{ title: '🗓️ Nuova Prenotazione!', text: 'Marco R. - Taglio + Barba', time: 'Martedì 15:30', color: 'emerald' },
       { title: '⭐ Nuova Recensione!', text: '"Servizio fantastico!"', time: '5.0 ★★★★★', color: 'amber' },
       { title: '💬 Nuovo Messaggio', text: '"Posso prenotare sabato?"', time: 'Anna M.', color: 'blue' }]
    : [{ title: '🛍️ Nuovo Ordine!', text: 'Crema Viso Bio - 2 pz', time: '€49,80', color: 'emerald' },
       { title: '⭐ Nuova Recensione!', text: '"Prodotto eccellente!"', time: '5.0 ★★★★★', color: 'amber' },
       { title: '📦 Ordine Spedito', text: 'Tracking: IT123456789', time: 'Consegna domani', color: 'blue' }];

  const positions = [{ top: '-10px', right: '-5px' }, { top: '40%', left: '-10px' }, { bottom: '80px', right: '-5px' }];
  const colors = { emerald: 'from-emerald-500 to-emerald-600', blue: 'from-blue-500 to-blue-600', amber: 'from-amber-500 to-amber-600' };

  return (
    <div className="relative mx-auto" style={{ maxWidth: '340px' }}>
      {notifications.map((n, i) => (
        <div key={i} className={`absolute z-20 transition-all duration-700 ${activeNotification === i ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'}`} style={positions[i]}>
          <div className={`bg-gradient-to-r ${colors[n.color]} rounded-xl p-2.5 shadow-2xl min-w-[160px]`}>
            <div className="text-white font-bold text-xs">{n.title}</div>
            <div className="text-white/80 text-[10px] mt-0.5">{n.text}</div>
            <div className="text-white/60 text-[10px] mt-1">{n.time}</div>
          </div>
        </div>
      ))}
      <div className="relative bg-gradient-to-b from-slate-700 to-slate-900 rounded-[2.5rem] p-2 shadow-2xl">
        <div className="bg-black rounded-[2rem] overflow-hidden">
          <div className="h-6 bg-black flex items-center justify-center"><div className="w-20 h-5 bg-black rounded-b-xl" /></div>
          <div className="bg-white" style={{ height: '520px' }}>
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-3 pb-16">
              <div className="flex items-center justify-between"><div className="text-white font-black text-lg">Skipres</div><Bell className="w-5 h-5 text-white" /></div>
              <div className="mt-2"><div className="bg-white/20 rounded-lg px-3 py-2 text-white/80 text-sm">🔍 Cerca {mockupType === 'services' ? 'servizi' : 'prodotti'}...</div></div>
            </div>
            <div className="px-3 -mt-10">
              <div className="bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden">
                <div className="h-28 relative overflow-hidden">
                  <img src={mockupType === 'services' ? IMAGES.barberShop : IMAGES.shop} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
                    <Badge className="bg-emerald-500 text-white text-xs border-0 px-2 py-0.5">{mockupType === 'services' ? 'Aperto' : 'Spedizione Gratis'}</Badge>
                  </div>
                </div>
                <div className="p-3">
                  <h4 className="font-bold text-slate-900 text-base">{mockupType === 'services' ? 'Barber Shop Milano' : 'Bio Shop Milano'}</h4>
                  <div className="flex items-center gap-1 mt-1">{[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}<span className="text-xs text-slate-500 ml-1">4.9</span></div>
                  <button className="w-full mt-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold py-2.5 rounded-lg text-sm">{mockupType === 'services' ? 'Prenota Ora' : 'Acquista Ora'}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute -z-10 inset-0 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-full blur-3xl scale-150" />
    </div>
  );
};

// Desktop Mockup
const DesktopMockup = ({ mockupType, isDark }) => (
  <div className="relative group">
    <div className={`relative backdrop-blur-xl border rounded-xl overflow-hidden shadow-2xl ${isDark ? 'bg-slate-900/80 border-slate-700' : 'bg-white border-slate-200'}`}>
      <div className={`px-3 py-2 flex items-center gap-2 border-b ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
        <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-500" /><div className="w-2.5 h-2.5 rounded-full bg-yellow-500" /><div className="w-2.5 h-2.5 rounded-full bg-green-500" /></div>
        <div className={`flex-1 flex items-center gap-2 rounded-lg px-3 py-1.5 ${isDark ? 'bg-slate-700/50' : 'bg-slate-200/50'}`}>
          <Shield className="w-3 h-3 text-emerald-500" /><span className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>skipres.com/{mockupType === 'services' ? 'barbershop-milano' : 'bioshop-milano'}</span>
        </div>
      </div>
      <div className="bg-gradient-to-b from-white to-slate-50">
        <div className="relative h-40 md:h-56 overflow-hidden">
          <img src={mockupType === 'services' ? IMAGES.barberShop : IMAGES.shop} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <Badge className="mb-2 bg-emerald-500 text-white border-0 text-xs">✓ Verificato Skipres</Badge>
            <h3 className="text-2xl md:text-3xl font-black text-white mb-1">{mockupType === 'services' ? 'Barber Shop Milano' : 'Bio Shop Milano'}</h3>
            <div className="flex items-center gap-2 text-white/80 text-xs">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Via Roma 123, Milano</span>
              <span className="flex items-center gap-1">{[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}<span className="ml-1">4.9</span></span>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-8 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-500" />{mockupType === 'services' ? 'I Nostri Servizi' : 'I Nostri Prodotti'}</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              {(mockupType === 'services' 
                ? [{ name: 'Taglio Classico', price: '€25', sub: '30 min' }, { name: 'Taglio + Barba', price: '€35', sub: '45 min' }]
                : [{ name: 'Crema Viso Bio', price: '€24,90', sub: 'Disponibile' }, { name: 'Olio Essenziale', price: '€18,50', sub: 'Disponibile' }]
              ).map((item, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 hover:border-emerald-300 transition-all">
                  <div className="flex items-center justify-between">
                    <div><div className="font-bold text-slate-900 text-sm">{item.name}</div><div className="text-xs text-slate-500">{item.sub}</div></div>
                    <div className="text-lg font-black text-emerald-600">{item.price}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200 rounded-xl p-4">
              <h4 className="text-lg font-bold text-slate-900 mb-3">{mockupType === 'services' ? 'Prenota Subito' : 'Acquista Ora'}</h4>
              <button className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold py-3 rounded-xl shadow-lg">{mockupType === 'services' ? 'Conferma Prenotazione' : 'Aggiungi al Carrello'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Live Notifications Demo
const LiveNotificationsDemo = ({ isDark, t }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => { const interval = setInterval(() => setActiveIndex(prev => (prev + 1) % 4), 2500); return () => clearInterval(interval); }, []);
  const notifications = [
    { icon: Calendar, title: 'Nuova Prenotazione', subtitle: 'Marco R. ha prenotato', value: '€35', color: 'emerald' },
    { icon: ShoppingBag, title: 'Nuovo Ordine', subtitle: 'Laura B. ha ordinato', value: '€24,90', color: 'blue' },
    { icon: Star, title: 'Nuova Recensione 5★', subtitle: '"Servizio fantastico!"', color: 'amber' },
    { icon: MessageCircle, title: 'Nuovo Messaggio', subtitle: 'Anna M.: "Posso prenotare?"', color: 'purple' }
  ];
  const colorMap = { emerald: 'bg-emerald-500/20 text-emerald-500', blue: 'bg-blue-500/20 text-blue-500', amber: 'bg-amber-500/20 text-amber-500', purple: 'bg-purple-500/20 text-purple-500' };

  return (
    <div className="grid md:grid-cols-2 gap-6 items-center max-w-5xl mx-auto">
      <div className="order-2 md:order-1 space-y-3">
        <h3 className={`text-xl md:text-2xl font-bold mb-4 ${t.text}`}>Il tuo business, sempre in tasca</h3>
        {notifications.map((n, i) => (
          <div key={i} onClick={() => setActiveIndex(i)} className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${activeIndex === i ? `${isDark ? 'bg-slate-800 border-emerald-500/50' : 'bg-emerald-50 border-emerald-300'} scale-[1.02]` : `${t.bgCard}`}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorMap[n.color]}`}><n.icon className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0"><div className={`font-bold text-sm ${t.text}`}>{n.title}</div><div className={`text-xs truncate ${t.textMuted}`}>{n.subtitle}</div></div>
            {n.value && <div className="text-emerald-500 font-bold">{n.value}</div>}
          </div>
        ))}
      </div>
      <div className="order-1 md:order-2 flex justify-center">
        <div className="relative">
          <div className="relative bg-slate-800 rounded-[2rem] p-2 shadow-2xl" style={{ width: '240px' }}>
            <div className="bg-black rounded-[1.75rem] overflow-hidden">
              <div className="h-5 bg-black flex justify-center items-end pb-0.5"><div className="w-16 h-3 bg-black rounded-b-lg" /></div>
              <div className="bg-slate-900 p-3" style={{ height: '400px' }}>
                <div className="space-y-2">{notifications.map((n, i) => (
                  <div key={i} className={`bg-slate-800 rounded-lg p-2 transition-all ${activeIndex === i ? 'opacity-100 border border-emerald-500/50' : 'opacity-50'}`}>
                    <div className="flex items-center gap-2"><n.icon className="w-4 h-4 text-emerald-400" /><div className="flex-1 min-w-0"><div className="text-white text-xs font-semibold truncate">{n.title}</div><div className="text-slate-400 text-xs truncate">{n.subtitle}</div></div></div>
                  </div>
                ))}</div>
                <div className="absolute bottom-16 left-3 right-3 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-lg p-3">
                  <div className="text-white/80 text-xs">Guadagni di oggi</div><div className="text-white text-xl font-black">€247,50</div>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute -inset-8 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-full blur-3xl -z-10" />
        </div>
      </div>
    </div>
  );
};

// Before/After
const BeforeAfterSection = ({ isDark, t }) => (
  <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
    <div className="relative">
      <div className={`border-2 rounded-2xl p-5 ${isDark ? 'bg-red-500/5 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-full text-xs font-bold mb-3"><X className="w-3 h-3" />SENZA Skipres</div>
          <div className="text-4xl mb-2">😰</div>
          <h3 className={`text-xl font-black ${t.text}`}>Il Vecchio Modo</h3>
        </div>
        <div className="space-y-2.5">
          {[{ icon: X, text: 'Nessuno ti trova online' }, { icon: Clock, text: 'Aspetti che i clienti vengano da soli' }, { icon: Users, text: 'Concorrenti ti rubano i clienti' }, { icon: X, text: 'Spendi in pubblicità senza risultati' }].map((item, i) => (
            <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${isDark ? 'bg-red-500/5 border border-red-500/20' : 'bg-red-100/50'}`}>
              <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center"><item.icon className="w-4 h-4 text-red-500" /></div>
              <span className={`text-sm ${t.textSecondary}`}>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className="relative">
      <div className={`border-2 rounded-2xl p-5 ${isDark ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'}`}>
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-2 bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-bold mb-3"><Check className="w-3 h-3" />CON Skipres</div>
          <div className="text-4xl mb-2">🚀</div>
          <h3 className={`text-xl font-black ${t.text}`}>Il Nuovo Modo</h3>
        </div>
        <div className="space-y-2.5">
          {[{ icon: Users, text: 'Noi ti portiamo nuovi clienti' }, { icon: TrendingUp, text: '50.000+ persone ti trovano' }, { icon: Sparkles, text: 'Vetrina professionale sempre online' }, { icon: Target, text: 'Investiamo NOI in pubblicità per te' }].map((item, i) => (
            <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${isDark ? 'bg-emerald-500/5 border border-emerald-500/20' : 'bg-emerald-100/50'}`}>
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center"><item.icon className="w-4 h-4 text-emerald-500" /></div>
              <span className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Testimonials
const TestimonialsSection = ({ isDark, t }) => {
  const testimonials = [
    { name: 'Marco Rossi', business: 'Barber Shop Milano', image: IMAGES.barberCutting, text: 'In un mese ho raddoppiato le prenotazioni!', result: '+127%', resultLabel: 'Prenotazioni', revenue: '€4.200/mese' },
    { name: 'Laura Bianchi', business: 'Centro Estetico Roma', image: IMAGES.beautySalon, text: 'Le prenotazioni arrivano automaticamente.', result: '+€2.400', resultLabel: 'Vendite Extra', revenue: '€6.800/mese' },
    { name: 'Giuseppe Verdi', business: 'Ristorante Napoli', image: IMAGES.restaurant, text: 'Gli ordini delivery sono aumentati del 40%!', result: '+40%', resultLabel: 'Delivery', revenue: '€8.500/mese' }
  ];
  return (
    <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
      {testimonials.map((item, i) => (
        <div key={i} className={`rounded-2xl p-4 border transition-all hover:-translate-y-1 ${isDark ? 'bg-slate-800/50 border-slate-700 hover:border-emerald-500/30' : 'bg-white border-slate-200 shadow-sm hover:shadow-md'}`}>
          <div className="h-32 rounded-xl overflow-hidden mb-4 -mt-1 -mx-1"><img src={item.image} alt="" className="w-full h-full object-cover" /></div>
          <div className="flex gap-0.5 mb-3">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
          <p className={`mb-4 text-sm ${t.textSecondary}`}>"{item.text}"</p>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold">{item.name.charAt(0)}</div>
            <div><div className={`font-bold text-sm ${t.text}`}>{item.name}</div><div className={`text-xs ${t.textMuted}`}>{item.business}</div></div>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-700/50">
            <div className={`rounded-lg p-2 text-center ${isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
              <div className="text-emerald-500 text-lg font-black">{item.result}</div><div className={`text-xs ${t.textMuted}`}>{item.resultLabel}</div>
            </div>
            <div className={`rounded-lg p-2 text-center ${isDark ? 'bg-slate-800/50 border border-slate-700' : 'bg-slate-50 border border-slate-200'}`}>
              <div className={`text-lg font-black ${t.text}`}>{item.revenue}</div><div className={`text-xs ${t.textMuted}`}>Fatturato</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// FAQ Section
const FAQSection = ({ isDark, t }) => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "Quanto tempo ci vuole per attivare la mia vetrina?",
      answer: "La tua vetrina sarà online in meno di 5 minuti! Basta compilare pochi dati sulla tua attività e sarai subito visibile a migliaia di potenziali clienti."
    },
    {
      question: "Posso annullare quando voglio?",
      answer: "Assolutamente sì! Non ci sono vincoli o penali. Puoi annullare il tuo abbonamento in qualsiasi momento direttamente dalla tua dashboard, senza dover contattare nessuno."
    },
    {
      question: "Come funziona il Programma Elit?",
      answer: "Con Elit, noi investiamo in pubblicità su Google e Facebook per portarti nuovi clienti. Tu paghi solo il 20% sulle vendite effettivamente generate dalle nostre campagne. E con la nostra Garanzia 6 Mesi, se non ricevi almeno 1 prenotazione o vendita entro 6 mesi, ti rimborsiamo tutto il costo della vetrina. Zero rischi, solo risultati!"
    },
    {
      question: "Il piano Solo Visibilità include le prenotazioni?",
      answer: "Sì! Con il piano Solo Visibilità i tuoi clienti possono prenotare servizi o ordinare prodotti direttamente dalla tua vetrina, 24 ore su 24, 7 giorni su 7."
    },
    {
      question: "Skipres funziona per il mio tipo di attività?",
      answer: "Skipres è perfetto per qualsiasi attività: parrucchieri, estetisti, barbieri, ristoranti, palestre, negozi, professionisti e molto altro. Se offri servizi o vendi prodotti, Skipres fa per te!"
    },
    {
      question: "Cosa succede dopo l'iscrizione?",
      answer: "Dopo l'acquisto, ti chiediamo solo alcune informazioni base. Poi ci pensiamo noi a tutto! Il nostro team ti contatterà in pochi minuti per raccogliere i dettagli necessari e configurare perfettamente la tua vetrina, assicurandosi che sia professionale e accattivante per i tuoi clienti. Tu non devi fare nulla: lavoriamo noi al posto tuo!"
    }
  ];

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div 
          key={index}
          className={`rounded-xl border overflow-hidden transition-all ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className={`w-full px-5 py-4 flex items-center justify-between text-left transition-colors ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'}`}
          >
            <span className={`font-semibold pr-4 ${t.text}`}>{faq.question}</span>
            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all ${openIndex === index ? 'bg-emerald-500 rotate-180' : isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
              <svg className={`w-3 h-3 ${openIndex === index ? 'text-white' : t.textMuted}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`}>
            <div className={`px-5 pb-4 ${t.textSecondary}`}>
              {faq.answer}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Pricing Card
const PricingCard = ({ selectedPlan, onContinue, isDark, t }) => {
  const monthlyPrice = 4.99, yearlyPrice = 49.90, originalMonthly = 9.99;
  return (
    <div className="relative">
      <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-2xl blur-lg opacity-50" />
      <div className={`relative border rounded-2xl p-6 md:p-8 overflow-hidden ${isDark ? 'bg-slate-900 border-emerald-500/30' : 'bg-white border-emerald-200'}`}>
        <div className="text-center mb-6">
          <Badge className="mb-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white border-0 px-3 py-1 text-xs"><Star className="w-3 h-3 mr-1" />Piano Più Popolare</Badge>
          <h3 className={`text-2xl md:text-3xl font-black mb-1 ${t.text}`}>Piano Solo Visibilità</h3>
          <p className={`text-sm ${t.textMuted}`}>Tutto ciò che ti serve per iniziare</p>
        </div>
        <div className={`text-center mb-6 py-4 border-y ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className="flex items-baseline justify-center gap-2">
            <span className={`text-2xl line-through ${t.textMuted}`}>€{originalMonthly.toFixed(2).replace('.', ',')}</span>
            <span className={`text-5xl md:text-6xl font-black ${t.text}`}>€{selectedPlan === 'yearly' ? (yearlyPrice / 12).toFixed(2).replace('.', ',') : monthlyPrice.toFixed(2).replace('.', ',')}</span>
            <span className={`text-xl ${t.textMuted}`}>/mese</span>
          </div>
          {selectedPlan === 'yearly' && <div className="mt-2"><div className="text-emerald-500 font-semibold text-sm">Fatturati €{yearlyPrice.toFixed(2).replace('.', ',')}/anno</div><Badge className="bg-red-500 text-white border-0 text-xs">🎁 Risparmi 50%</Badge></div>}
          {selectedPlan === 'monthly' && <Badge className="mt-2 bg-red-500 text-white border-0 text-xs">🎁 50% di sconto</Badge>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
          {['Vetrina online professionale', 'Visibilità su 50.000+ utenti', 'Prenotazioni e ordini 24/7', 'Sistema recensioni', 'Chat integrata', 'Notifiche in tempo reale', 'Link condivisibile', 'Supporto H24'].map((f, i) => (
            <div key={i} className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'}`}>
              <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center"><Check className="w-3 h-3 text-emerald-500" /></div>
              <span className={`text-sm ${t.textSecondary}`}>{f}</span>
            </div>
          ))}
        </div>
        <Button size="lg" onClick={onContinue} className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white text-lg py-6 rounded-xl shadow-xl hover:shadow-emerald-500/30 transition-all hover:scale-[1.02] group" data-testid="pricing-cta-button">
          Attiva Ora - 50% di Sconto<ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </Button>
        <div className={`flex flex-wrap items-center justify-center gap-4 mt-4 text-xs ${t.textMuted}`}>
          <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-emerald-500" />Pagamento sicuro</span>
          <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" />Annulla quando vuoi</span>
        </div>
        
        {/* Garanzia Elit */}
        <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className={`rounded-xl p-3 ${isDark ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className={`font-bold text-sm mb-1 ${t.text}`}>🛡️ Garanzia Elit 6 Mesi</div>
                <p className={`text-xs leading-relaxed ${t.textSecondary}`}>
                  Attiva il <span className="text-emerald-500 font-semibold">Programma Elit</span> e se non ricevi <span className="text-emerald-500 font-semibold">almeno 1 prenotazione o vendita</span> entro 6 mesi, ti <span className="text-emerald-500 font-semibold">rimborsiamo tutto</span>. Non hai niente da perdere!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Homepage;
